import React, {useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {findImageGroupBy} from '../../../utils/image-groups-utils'
import {Flex, useMediaQuery, useMultiStyleConfig} from '@chakra-ui/react'
import {ChevronLeft, ChevronRight} from '../../icons'
import GallerySlider from './gallery-slider'
import ProductDetailGallery from './product-detail-gallery'
import {remCalc} from '../../../theme/_utils'
import {handleChevronStyles, LARGE, SMALL} from './utils'
import {parseAndSanitizeJSON} from '../../../utils/utils'
import YoutubeIframe from '../../youtube-video'
import {useIntl} from 'react-intl'

const messages = {
    previousImage: {
        id: 'gallery.previousImage',
        defaultMessage: 'Previous image',
    },
    nextImage: {
        id: 'gallery.nextImage',
        defaultMessage: 'Next image',
    },
}

const GalleryBody = ({
    imageGroups = [],
    selectedVariationAttributes = {},
    lazy = false,
    openModal,
    variant,
    videoUrl,
    setIsModalGallerySwiper,
    isModalGallerySwiper,
    isModalOpen,
    modalInitValue,
    powerImages = [],
    subModelIdentifier,
}) => {
    const intl = useIntl()
    const styles = useMultiStyleConfig('ImageGallery', {variant})
    const [isDesktop] = useMediaQuery(`(min-width: ${remCalc(1024)})`)
    const [isTablet] = useMediaQuery(`(min-width: ${remCalc(768)})`)
    const swiperRef = useRef(null)
    const scrollRef = useRef(null)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [scrollPosition, setScrollPosition] = useState(0)
    const stop = useRef(null)

    const stopPropagation = () => {
        stop.current = true
    }

    // Get the 'hero' image for the current variation.
    const heroImageGroup = useMemo(() => {
        const standardGroup = findImageGroupBy(imageGroups, {
            viewType: LARGE,
            selectedVariationAttributes,
        })

        if (powerImages?.length > 0) {
            return {
                images: [
                    ...(standardGroup?.images || []),
                    ...powerImages.map((image, index) => ({
                        alt: image.alt || `Power Image ${index + 1}`,
                        disBaseLink: image.imageLink || image.url,
                        link: image.imageLink || image.url,
                        title: image.title || `Power Image ${index + 1}`,
                    })),
                ],
                viewType: LARGE,
            }
        }

        return standardGroup
    }, [selectedVariationAttributes, powerImages])

    const thumbnailImageGroup = useMemo(() => {
        const standardGroup = findImageGroupBy(imageGroups, {
            viewType: SMALL,
            selectedVariationAttributes,
        })

        if (powerImages?.length > 0) {
            return {
                images: [
                    ...(standardGroup?.images || []),
                    ...powerImages.map((image, index) => ({
                        alt: image.alt || `Power Image ${index + 1}`,
                        disBaseLink: image.imageLink || image.url,
                        link: image.imageLink || image.url,
                        title: image.title || `Power Image ${index + 1}`,
                    })),
                ],
                viewType: SMALL,
            }
        }

        return standardGroup
    }, [selectedVariationAttributes, powerImages])

    const video = parseAndSanitizeJSON(videoUrl)?.[0]
    const heroImage = heroImageGroup?.images?.[selectedIndex] || selectedIndex === video?.code

    const thumbnailImages = thumbnailImageGroup?.images || []
    const loadingStrategy = lazy ? 'lazy' : 'eager'

    const handleGalleryNavigation = (move) => {
        if (!stop.current) {
            const newIdx = move ? selectedIndex + 1 : selectedIndex - 1
            const lastIdx = thumbnailImages.length - 1
            const nextIdx = newIdx > lastIdx ? 0 : newIdx
            const prevIdx = newIdx < 0 ? lastIdx : newIdx
            setSelectedIndex(move ? nextIdx : prevIdx)

            if (selectedIndex !== 0 && selectedIndex !== lastIdx) {
                if (isDesktop) {
                    setScrollPosition(
                        move
                            ? (scrollRef.current.scrollTop += 88)
                            : (scrollRef.current.scrollTop -= 88),
                    )
                } else if (isTablet) {
                    setScrollPosition(
                        move
                            ? (scrollRef.current.scrollLeft += 86)
                            : (scrollRef.current.scrollLeft -= 86),
                    )
                } else {
                    setScrollPosition(
                        move
                            ? (scrollRef.current.scrollLeft += 66)
                            : (scrollRef.current.scrollLeft -= 66),
                    )
                }
            }
            if (move) {
                if (selectedIndex === lastIdx) setSelectedIndex(lastIdx)
                else {
                    swiperRef && swiperRef.current && swiperRef.current.slideTo(nextIdx)
                    setSelectedIndex(nextIdx)
                }
            } else {
                if (selectedIndex === 0) setSelectedIndex(0)
                else {
                    swiperRef && swiperRef.current && swiperRef.current.slideTo(prevIdx)
                    setSelectedIndex(prevIdx)
                }
            }
        } else {
            stop.current = false
        }
    }

    return (
        <Flex direction={{base: 'column-reverse', lg: 'row'}}>
            <GallerySlider
                swiperRef={swiperRef}
                scrollRef={scrollRef}
                loadingStrategy={loadingStrategy}
                videoUrl={videoUrl}
                styles={styles}
                scrollPosition={scrollPosition}
                setScrollPosition={setScrollPosition}
                selectedIndex={selectedIndex}
                setSelectedIndex={setSelectedIndex}
                thumbnailImages={thumbnailImages}
                handleGalleryNavigation={handleGalleryNavigation}
                stopPropagation={stopPropagation}
                openModal={openModal}
                setIsModalGallerySwiper={setIsModalGallerySwiper}
                isModalOpen={isModalOpen}
                modalInitValue={modalInitValue}
            />
            {!isModalGallerySwiper && isModalOpen && (
                <Flex data-testid="image-gallery-video" {...styles.videoHero}>
                    <YoutubeIframe title={video?.title} videoSrc={video?.videolink} />
                </Flex>
            )}
            {(isModalGallerySwiper || !isModalOpen) && heroImage && (
                <Flex
                    {...styles.heroImageGroup}
                    data-testid="hero-image-group"
                    data-ab={!isModalOpen ? 'gallery-collapsed-slider' : 'gallery-exploded-slider'}
                    justifyContent={
                        selectedIndex === video?.code
                            ? 'center'
                            : {...styles.heroImageGroup.justifyContent}
                    }
                >
                    {selectedIndex !== video?.code && (
                        <Flex {...styles.heroLeftRightArrowsContainer}>
                            <Flex
                                {...styles.heroLeftRightArrows}
                                {...handleChevronStyles(
                                    'horizontal',
                                    thumbnailImages.length,
                                    selectedIndex,
                                    0,
                                )}
                                className="swiper-button-prev"
                                data-testid="swiper-button-prev"
                                tabIndex={0}
                                role="button"
                                aria-label={intl.formatMessage(messages.previousImage)}
                                onClick={() => handleGalleryNavigation(false)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        handleGalleryNavigation(false)
                                    }
                                }}
                            >
                                <ChevronLeft {...styles.heroLeftRightArrows} />
                            </Flex>
                        </Flex>
                    )}

                    <ProductDetailGallery
                        heroImageGroup={heroImageGroup}
                        loadingStrategy={loadingStrategy}
                        openModal={openModal}
                        videoUrl={videoUrl}
                        handleGalleryNavigation={handleGalleryNavigation}
                        selectedIndex={selectedIndex}
                        swiperRef={swiperRef}
                        styles={styles}
                        variant={variant}
                        setIsModalGallerySwiper={setIsModalGallerySwiper}
                        isModalOpen={isModalOpen}
                        subModelIdentifier={subModelIdentifier}
                    />

                    {selectedIndex !== video?.code && (
                        <Flex {...styles.heroLeftRightArrowsContainer}>
                            <Flex
                                {...styles.heroLeftRightArrows}
                                {...handleChevronStyles(
                                    'horizontal',
                                    thumbnailImages.length,
                                    selectedIndex,
                                    thumbnailImages.length - 1,
                                )}
                                className="swiper-button-next"
                                data-testid="swiper-button-next"
                                tabIndex={0}
                                role="button"
                                aria-label={intl.formatMessage(messages.nextImage)}
                                onClick={() => handleGalleryNavigation(true)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault()
                                        handleGalleryNavigation(true)
                                    }
                                }}
                            >
                                <ChevronRight {...styles.heroLeftRightArrows} />
                            </Flex>
                        </Flex>
                    )}
                </Flex>
            )}
        </Flex>
    )
}

GalleryBody.propTypes = {
    imageGroups: PropTypes.array,
    selectedVariationAttributes: PropTypes.object,
    lazy: PropTypes.bool,
    openModal: PropTypes.func,
    variant: PropTypes.string,
    videoUrl: PropTypes.string,
    setIsModalGallerySwiper: PropTypes.func,
    isModalGallerySwiper: PropTypes.bool,
    isModalOpen: PropTypes.bool,
    modalInitValue: PropTypes.number,
    powerImages: PropTypes.array,
    subModelIdentifier: PropTypes.string,
}

export default GalleryBody
